import { HeraldClient } from '@herald/client';
import {
  termsDigest, verifyConsent, npubEncode, parseUri, isExpired,
} from '@herald/core';
import { BrowserVault } from '../shared/vault.js';
import { loadConfig } from '../shared/config.js';
import { formatCapCs, formatRetentionCs } from '../shared/format.js';
import { renderMarkdown, markdownSnippet, setStrings } from '../shared/markdown.js';
import { encryptBackup, decryptBackup, backupFilename, MIN_PASSPHRASE_LENGTH } from '../shared/backup.js';
import { $, esc, onAction, shortNpub, formatDate, timeAgo, toast, withBusy } from '../shared/ui.js';

/**
 * Herald subscriber PWA.
 *
 * A unified inbox across every channel the user consented to -- the mail-client
 * shape people already know, over a protocol where a message that is not from
 * a sender you subscribed to cannot arrive at all.
 *
 * Read, starred and archived state is deliberately **local to this device**.
 * Syncing it would mean telling a server which messages this person opened,
 * which is exactly the tracking that anonymous_ok channels exist to prevent
 * (HP-02). The settings screen says so rather than leaving it implicit.
 */

// The renderer is language-agnostic; this app is Czech.
setStrings({
  blockedImageTitle: 'Obrázek z jiného serveru byl zablokován',
  blockedImage: (alt) => `[zablokovaný obrázek: ${alt || 'bez popisu'}]`,
});

const vault = new BrowserVault('herald.subscriber.v1');
let config = { relays: [`${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}`], wellKnownOrigin: null };

let client = null;
let screen = 'inbox';
let ctx = {};
const stack = [];

let messages = [];        // merged, newest first
/**
 * `loading` is true from the moment the app decides it has something to fetch,
 * not from the moment the fetch starts. The first paint used to happen before
 * `refresh()` set the flag, so a returning subscriber saw "no messages" for a
 * second and then watched them appear -- indistinguishable from having been
 * unsubscribed.
 */
let loading = false;
let query = '';
let filter = 'all';       // all | unread | starred
let installPrompt = null;

const subs = () => vault.state?.subscriptions ?? [];
const relays = () => (vault.state?.relays?.length ? vault.state.relays : config.relays);
const flags = () => (vault.state.flags ??= {});
const flagsFor = (id) => flags()[id] ?? {};

function setFlag(id, patch) {
  flags()[id] = { ...flagsFor(id), ...patch };
  vault.save();
}

function connect() {
  client?.close();
  client = new HeraldClient({
    secretKey: vault.secretKey,
    relays: relays(),
    // null means real HTTPS resolution against the domain the sender claims;
    // a value here is a development override (see shared/config.js).
    wellKnownOrigin: config.wellKnownOrigin,
  });
}

// ------------------------------------------------------------- navigation

function go(next, data = {}, { replace = false } = {}) {
  if (!replace) stack.push({ screen, ctx });
  screen = next;
  ctx = data;
  render();
}

function back() {
  const previous = stack.pop();
  if (!previous) return go('inbox', {}, { replace: true });
  screen = previous.screen;
  ctx = previous.ctx;
  render();
}

function tab(next) {
  stack.length = 0;
  screen = next;
  ctx = {};
  render();
}

// ------------------------------------------------------------- data

async function refresh({ quiet = false } = {}) {
  if (!subs().length) { messages = []; loading = false; return render(); }
  loading = true;
  render();

  const collected = await Promise.all(subs().map(async (sub) => {
    try {
      const info = await client.inspect({ root: sub.root, relays: sub.relays, channel: sub.slug });
      const current = info.channels.find((c) => c.slug === sub.slug);

      // Keep the stored copy honest: a domain can stop verifying, and terms change.
      sub.domainVerified = info.domainVerified;
      sub.termsChanged = current ? termsDigest(current) !== sub.terms : false;
      sub.currentCap = current?.cap ?? sub.cap;

      const found = await client.fetchMessages({
        root: sub.root, manifestEvent: info.manifestEvent, channel: sub.slug, relays: sub.relays,
      });
      return found.map((m) => ({ ...m, sub, senderName: sub.senderName ?? sub.npub }));
    } catch (err) {
      return [{
        id: `error:${sub.root}:${sub.slug}`, error: err.message, unreachable: true,
        createdAt: 0, subject: '', body: '', verified: false, sub, senderName: sub.senderName ?? sub.npub,
      }];
    }
  }));

  vault.save();
  messages = collected.flat()
    .filter((m) => !m.unreachable)
    .filter((m) => !isExpired(m.event))
    .sort((a, b) => b.createdAt - a.createdAt);

  const failures = collected.flat().filter((m) => m.unreachable);
  loading = false;
  render();
  if (failures.length && !quiet) toast(`${failures.length} kanál(y) se nepodařilo načíst`, 'error');
}

function visibleMessages() {
  const needle = query.trim().toLowerCase();
  return messages.filter((m) => {
    const f = flagsFor(m.id);
    if (f.archived && filter !== 'starred') return false;
    if (filter === 'unread' && f.read) return false;
    if (filter === 'starred' && !f.starred) return false;
    if (!needle) return true;
    return [m.subject, m.body, m.senderName, m.sub.channelName]
      .some((field) => String(field ?? '').toLowerCase().includes(needle));
  });
}

const unreadCount = () => messages.filter((m) => !flagsFor(m.id).read && !flagsFor(m.id).archived).length;

// ------------------------------------------------------------- chrome

function renderChrome() {
  const topbar = $('#topbar');
  const tabbar = $('#tabbar');

  const backButton = stack.length
    ? `<button class="iconbtn" data-action="back" aria-label="Zpět">‹</button>` : '';

  if (screen === 'inbox') {
    topbar.innerHTML = `
      <div class="search">
        <span class="search__icon" aria-hidden="true">⌕</span>
        <input type="text" id="q" placeholder="Hledat ve zprávách" value="${esc(query)}"
               autocomplete="off" spellcheck="false" enterkeyhint="search">
        ${query ? `<button class="iconbtn" data-action="clear-search" aria-label="Vymazat">×</button>` : ''}
        <button class="iconbtn" data-action="settings" aria-label="Nastavení">⚙</button>
      </div>
      <div class="chips">
        <button class="chip" data-action="filter" data-value="all"     aria-pressed="${filter === 'all'}">Vše</button>
        <button class="chip" data-action="filter" data-value="unread"  aria-pressed="${filter === 'unread'}">Nepřečtené</button>
        <button class="chip" data-action="filter" data-value="starred" aria-pressed="${filter === 'starred'}">S hvězdičkou</button>
        <button class="chip" data-action="refresh">${loading ? 'Načítám…' : 'Obnovit'}</button>
      </div>`;
  } else {
    const titles = {
      senders: 'Odesílatelé', scan: 'Přidat odesílatele', settings: 'Nastavení',
      consent: 'Přihlásit se k odběru?', pick: 'Vyberte kanál', sender: ctx.sub?.channelName ?? 'Kanál',
    };
    const actions = screen === 'message' ? `
      <button class="iconbtn iconbtn--star ${flagsFor(ctx.message?.id).starred ? 'is-on' : ''}"
              data-action="toggle-star" data-id="${esc(ctx.message?.id ?? '')}" aria-label="Hvězdička">★</button>
      <button class="iconbtn" data-action="archive" data-id="${esc(ctx.message?.id ?? '')}" aria-label="Archivovat">▾</button>`
      : '';
    topbar.innerHTML = `
      <div class="topbar__row">
        ${backButton}
        <h1 class="topbar__title">${esc(screen === 'message' ? '' : (titles[screen] ?? 'Herald'))}</h1>
        ${actions}
      </div>`;
  }

  const showTabs = ['inbox', 'senders', 'scan'].includes(screen);
  tabbar.hidden = !showTabs;
  if (showTabs) {
    const unread = unreadCount();
    tabbar.innerHTML = `
      <button data-action="tab" data-value="inbox" ${screen === 'inbox' ? 'aria-current="page"' : ''}>
        <span class="ico" aria-hidden="true">✉</span>Zprávy${unread ? `<span class="pill">${unread}</span>` : ''}</button>
      <button data-action="tab" data-value="senders" ${screen === 'senders' ? 'aria-current="page"' : ''}>
        <span class="ico" aria-hidden="true">◉</span>Odesílatelé</button>
      <button data-action="tab" data-value="scan" ${screen === 'scan' ? 'aria-current="page"' : ''}>
        <span class="ico" aria-hidden="true">＋</span>Přidat</button>`;
  }
}

function render() {
  if (!vault.exists) { screen = 'onboarding'; }
  renderChrome();
  const host = $('#screen');
  host.classList.toggle('screen--flush', screen === 'inbox');
  host.innerHTML = ({
    onboarding: viewOnboarding, inbox: viewInbox, message: viewMessage,
    senders: viewSenders, sender: viewSender, scan: viewScan,
    consent: viewConsent, pick: viewPick, settings: viewSettings,
  }[screen] ?? viewInbox)();
  if (screen === 'scan') startScanner();
}

// ------------------------------------------------------------- views

function viewOnboarding() {
  return `
    <div class="card" style="margin-top:24px">
      <h2>Zprávy, které vám nikdo nepodstrčí</h2>
      <p class="dim small">V Heraldu nemáte adresu ani účet. Vaší identitou je klíč,
      který vznikne tady na zařízení a nikam se neposílá. Firma vám nemůže napsat,
      dokud jí to sami podepsaným souhlasem nedovolíte — a zpráva, která není od ní,
      se k vám nedostane.</p>
      <button class="btn" data-action="create-identity">Začít</button>
    </div>
    <div class="card">
      <h3 style="margin-top:0">Obnovit ze zálohy</h3>
      <p class="dim small">Zálohu jste si stáhl z jiného zařízení. Je zašifrovaná heslem.</p>
      <input type="file" id="backupfile" accept="application/json,.json" hidden>
      <button class="btn btn--ghost" data-action="pick-backup">Vybrat soubor se zálohou</button>
      <div id="restore-area"></div>
    </div>
    <div class="card">
      <h3 style="margin-top:0">Nebo vložit klíč přímo</h3>
      <label for="import">Tajný klíč (nsec1… nebo hex)</label>
      <input type="text" id="import" placeholder="nsec1…" autocomplete="off" spellcheck="false">
      <div style="height:12px"></div>
      <button class="btn btn--ghost" data-action="import-identity">Importovat</button>
    </div>`;
}

function viewInbox() {
  if (!subs().length) {
    return `<div class="empty" style="padding:0 20px">
      <div class="card" style="margin:32px 12px">
        <h2>Zatím žádní odesílatelé</h2>
        <p class="dim small">Načtěte QR kód firmy, jejíž zprávy chcete dostávat.
        Uvidíte předem, kdo to je a co si smí dovolit.</p>
        <button class="btn" data-action="tab" data-value="scan">Načíst QR kód</button>
      </div></div>`;
  }

  const list = visibleMessages();
  if (!list.length) {
    return `<div class="empty"><span class="empty__icon">${loading ? '◌' : '◻'}</span>
      ${loading ? 'Načítám zprávy…' : query ? 'Nic nenalezeno.' : 'Žádné zprávy.'}</div>`;
  }

  // Key loss is irreversible, so this is not a dismissible hint.
  const backupNag = vault.state.backedUpAt ? '' : `
    <button class="note note--warn" data-action="settings"
            style="margin:8px 12px;display:block;width:calc(100% - 24px);text-align:left;border-width:1px;cursor:pointer">
      <strong>Zazálohujte si klíč</strong>
      Bez zálohy o odběry nevratně přijdete — obnova hesla neexistuje. Ťukněte pro zálohu.
    </button>`;

  const warnings = backupNag + subs().filter((s) => s.termsChanged).map((s) => `
    <div class="note note--warn" style="margin:8px 12px">
      <strong>${esc(s.channelName ?? s.slug)}: podmínky se změnily</strong>
      Nyní ${esc(formatCapCs(s.currentCap))}. Váš podepsaný souhlas je nekryje.
    </div>`).join('');

  return warnings + `<div class="list">${list.map(rowHtml).join('')}</div>`;
}

function rowHtml(m) {
  const f = flagsFor(m.id);
  const av = avatarFor(m.sub.root, m.senderName);
  return `
    <button class="row ${f.read ? '' : 'is-unread'}" data-action="open-message" data-id="${esc(m.id)}">
      <span class="avatar" style="background:${av.color}" aria-hidden="true">${esc(av.initial)}</span>
      <span class="row__main">
        <span class="row__top">
          <span class="row__sender">${esc(m.senderName)}</span>
          <span class="row__time">${esc(timeAgo(m.createdAt))}</span>
        </span>
        <span class="row__subject">${esc(m.subject || '(bez předmětu)')}</span>
        <span class="row__snippet">${esc(markdownSnippet(m.body, 80) || '—')}</span>
        <span class="row__meta">
          ${m.verified ? '' : '<span class="badge badge--danger">neověřeno</span>'}
          ${m.direct ? '<span class="badge badge--mute">jen pro vás</span>' : ''}
          ${(m.attachments?.length ?? 0) > 0 ? `<span class="clip">📎 ${m.attachments.length}</span>` : ''}
        </span>
      </span>
      <span class="row__tail">
        ${f.read ? '' : '<span class="dot" aria-label="nepřečteno"></span>'}
        ${f.starred ? '<span style="color:var(--star)">★</span>' : ''}
      </span>
    </button>`;
}

function viewMessage() {
  const m = ctx.message;
  if (!m) return `<div class="empty">Zpráva není dostupná.</div>`;
  const av = avatarFor(m.sub.root, m.senderName);

  const unverified = m.verified ? '' : `
    <div class="note note--danger">
      <strong>Tuto zprávu nelze ověřit</strong>
      ${esc(m.error ?? 'Podpis nevede ke klíči, který jste naskenovali.')}
      Neklikejte na odkazy a nikomu neposílejte údaje.
    </div>`;

  const attachments = (m.attachments?.length ?? 0) === 0 ? '' : `
    <h3>Přílohy</h3>
    ${m.attachments.map((a, i) => `
      <button class="attach" data-action="download" data-index="${i}">
        <span class="attach__icon" aria-hidden="true">📎</span>
        <span class="attach__main">
          <span class="attach__name">${esc(a.filename)}</span>
          <span class="attach__meta">${esc(a.mime)} · ${formatBytes(a.size)}</span>
        </span>
        <span class="attach__icon" aria-hidden="true">↓</span>
      </button>`).join('')}
    <p class="small dim">Příloha se po stažení ověřuje proti podpisu odesílatele.
    Když relay podstrčí jiná data, poznáte to.</p>`;

  return `
    ${unverified}
    <div class="detail__from">
      <span class="avatar" style="background:${av.color}" aria-hidden="true">${esc(av.initial)}</span>
      <span class="detail__from-main">
        <div class="detail__name">${esc(m.senderName)}</div>
        <div class="small dim">${esc(m.sub.domain ?? 'bez domény')}
          ${m.sub.domainVerified ? '<span class="badge badge--ok">✓ ověřeno</span>'
                                 : '<span class="badge badge--danger">neověřeno</span>'}</div>
      </span>
    </div>
    <div class="detail__subject">${esc(m.subject || '(bez předmětu)')}</div>
    <div class="small dim" style="margin:-6px 0 18px">${esc(formatDate(m.createdAt))} ·
      ${esc(m.sub.channelName ?? m.sub.slug)}</div>

    <div class="body">${m.verified ? renderBody(m) : ''}</div>
    ${attachments}

    <details class="verify">
      <summary>Jak byla zpráva ověřena</summary>
      <dl>
        <dt>Podpis</dt><dd>${m.verified ? `platný (${esc(m.via === 'root' ? 'hlavní klíč' : 'delegovaný klíč')})` : 'neplatný'}</dd>
        <dt>Odesílatel</dt><dd class="mono">${esc(m.sub.npub)}</dd>
        <dt>Doména</dt><dd>${esc(m.sub.domain ?? '—')} ${m.sub.domainVerified ? '✓' : '✗'}</dd>
        <dt>Obsah</dt><dd>${m.direct ? 'šifrováno jen pro vás' : 'veřejný newsletter'}</dd>
        <dt>Souhlas</dt><dd>${m.sub.receiptVerified ? 'firmou potvrzen' : 'bez potvrzení'}</dd>
        <dt>Limit</dt><dd>${esc(formatCapCs(m.sub.cap))}</dd>
      </dl>
    </details>`;
}

/** Only ever called for a verified message. */
function renderBody(m) {
  const isMarkdown = m.contentType === 'text/markdown';
  return isMarkdown ? renderMarkdown(m.body) : `<p>${esc(m.body).replace(/\n/g, '<br>')}</p>`;
}

function viewSenders() {
  if (!subs().length) {
    return `<div class="empty"><span class="empty__icon">◉</span>Žádní odesílatelé.</div>`;
  }
  return subs().map((s) => {
    const av = avatarFor(s.root, s.senderName ?? s.npub);
    const count = messages.filter((m) => m.sub.root === s.root && m.sub.slug === s.slug).length;
    return `
      <button class="card card--tap" data-action="open-sender" data-key="${esc(s.root)}|${esc(s.slug)}">
        <div style="display:flex;gap:11px;align-items:center">
          <span class="avatar" style="background:${av.color}" aria-hidden="true">${esc(av.initial)}</span>
          <span style="flex:1;min-width:0">
            <h2>${esc(s.channelName ?? s.slug)}</h2>
            <div class="small dim">${esc(s.senderName ?? shortNpub(s.npub))} · ${count} zpráv</div>
          </span>
        </div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:11px">
          ${s.domainVerified ? `<span class="badge badge--ok">✓ ${esc(s.domain)}</span>`
                             : '<span class="badge badge--danger">neověřeno</span>'}
          <span class="badge badge--mute">${esc(formatCapCs(s.cap))}</span>
          ${s.termsChanged ? '<span class="badge badge--warn">změněné podmínky</span>' : ''}
        </div>
      </button>`;
  }).join('');
}

function viewSender() {
  const s = ctx.sub;
  const audit = ctx.audit;
  return `
    <div class="card">
      <div class="sender__name">${esc(s.channelName ?? s.slug)}</div>
      <div class="sender__domain">
        <span class="dim">${esc(s.senderName ?? '')} · ${esc(s.domain ?? 'bez domény')}</span>
        ${s.domainVerified ? '<span class="badge badge--ok">✓ ověřeno</span>'
                           : '<span class="badge badge--danger">neověřeno</span>'}
      </div>
      <dl class="terms">
        <div class="terms__row terms__cap"><dt>Dohodnutý limit</dt><dd>${esc(formatCapCs(s.cap))}</dd></div>
        <div class="terms__row"><dt>Přihlášeno</dt><dd>${esc(formatDate(s.subscribedAt))}</dd></div>
        <div class="terms__row"><dt>Souhlas potvrzen</dt><dd>${s.receiptVerified ? 'ano' : 'ne'}</dd></div>
        <div class="terms__row"><dt>Uchování</dt><dd>${esc(formatRetentionCs(s.retentionDays))}</dd></div>
      </dl>
    </div>
    ${s.termsChanged ? `<div class="note note--warn">
      <strong>Podmínky se změnily</strong>Nyní ${esc(formatCapCs(s.currentCap))}.
      Váš podepsaný souhlas je nekryje.</div>` : ''}
    ${audit?.breach ? `<div class="note note--danger">
      <strong>Firma překročila svůj limit</strong>${esc(audit.breach)}</div>` : ''}
    <button class="btn btn--danger" data-action="unsubscribe">Odhlásit se z odběru</button>
    <p class="small dim">Odhlášení podepisujete vy. Jakákoli pozdější zpráva
    na tento kanál je prokazatelné porušení.</p>`;
}

function viewScan() {
  return `
    <div class="card">
      <h2>Načtěte kód firmy</h2>
      <p class="dim small">Nic se zatím neodesílá. Na další obrazovce uvidíte,
      kdo firma je a co si smí dovolit.</p>
      <video id="cam" playsinline muted></video>
      <div id="cam-status" class="small dim" style="margin-top:9px"></div>
    </div>
    <div class="card">
      <label for="uri">…nebo vložte odkaz ručně</label>
      <input type="text" id="uri" placeholder="herald:sub?k=npub1…" autocomplete="off" spellcheck="false">
      <div style="height:12px"></div>
      <button class="btn btn--ghost" data-action="paste-uri">Pokračovat</button>
    </div>`;
}

function viewPick() {
  const { info } = ctx;
  return `
    <div class="card">
      <div class="sender__name">${esc(info.profile?.name ?? 'Firma bez jména')}</div>
      <div class="sender__domain"><span class="dim">${esc(info.domain ?? 'bez domény')}</span>
        ${info.domainVerified ? '<span class="badge badge--ok">✓ ověřeno</span>'
                              : '<span class="badge badge--danger">neověřeno</span>'}</div>
    </div>
    ${ctx.missing ? `<div class="note note--warn">
      <strong>Odkaz míří na kanál „${esc(ctx.missing)}“, který firma nezveřejňuje</strong>
      Mohl se změnit, nebo odkaz nepochází od této firmy. Vyberte kanál sám,
      nebo se nepřihlašujte.</div>` : ''}
    <h3>Vyberte kanál</h3>
    ${info.channels.map((c) => `
      <button class="card card--tap" data-action="pick" data-slug="${esc(c.slug)}">
        <h2>${esc(c.name)}</h2>
        ${c.description ? `<div class="small dim" style="margin:3px 0 9px">${esc(c.description)}</div>` : ''}
        <div style="display:flex;gap:6px;flex-wrap:wrap">
          <span class="badge badge--mute">${esc(typeLabel(c.type))}</span>
          <span class="badge badge--ok">${esc(formatCapCs(c.cap))}</span>
        </div>
      </button>`).join('')}`;
}

/** The consent screen: identity, then whether that claim is verified, then terms. */
function viewConsent() {
  const { info, channel } = ctx;
  const verified = info.domainVerified;
  const anonymous = channel.privacy === 'anonymous_ok';

  return `
    <div class="card">
      <div class="sender__name">${esc(info.profile?.name ?? 'Firma bez uvedeného jména')}</div>
      <div class="sender__domain">
        <span>${esc(info.domain ?? 'bez domény')}</span>
        ${verified ? '<span class="badge badge--ok">✓ ověřeno</span>'
                   : '<span class="badge badge--danger">neověřeno</span>'}
      </div>
      <div class="mono dim" style="margin-top:8px">${esc(info.npub)}</div>
    </div>
    ${info.admission ? `<div class="note note--${info.admission.status === 'admitted' ? 'ok' : 'danger'}">
      <strong>${info.admission.status === 'admitted'
        ? 'Provozovatel relaye tuto firmu schválil'
        : `Provozovatel relaye tuto firmu ${info.admission.status === 'suspended' ? 'pozastavil' : 'zamítl'}`}</strong>
      ${esc(info.admission.note || 'Bez poznámky.')}
      Je to nezávislý signál od provozovatele — nenahrazuje ověření domény.
    </div>` : ''}
    ${verified ? '' : `<div class="note note--danger">
      <strong>Totožnost firmy se nepodařilo ověřit</strong>
      ${esc(info.domainError ?? 'Firma neuvádí doménu, takže nelze zjistit, kdo za klíčem stojí.')}
      Přihlaste se jen tehdy, pokud kód máte z důvěryhodného zdroje.</div>`}
    ${(info.warnings ?? []).map((w) => `<div class="note note--warn">${esc(w)}</div>`).join('')}
    <div class="card">
      <h2>${esc(channel.name)}</h2>
      ${channel.description ? `<p class="dim small">${esc(channel.description)}</p>` : ''}
      <dl class="terms">
        <div class="terms__row terms__cap"><dt>Nejvýše</dt><dd>${esc(formatCapCs(channel.cap))}</dd></div>
        <div class="terms__row"><dt>Typ</dt><dd>${esc(typeLabel(channel.type))}</dd></div>
        <div class="terms__row"><dt>Soukromí</dt><dd>${anonymous ? 'anonymní odběr' : 'firma vás bude znát'}</dd></div>
        <div class="terms__row"><dt>Uchování zpráv</dt><dd>${esc(formatRetentionCs(channel.retentionDays))}</dd></div>
      </dl>
      <p class="small dim" style="margin-top:11px">
        ${anonymous
          ? 'Firma se nedozví, že jste se přihlásili, ani zda jste zprávy otevřeli.'
          : 'Tento kanál posílá zprávy adresované vám, takže firma bude znát váš klíč. Obsah je šifrovaný.'}
      </p>
      <p class="small dim">Limit je součástí toho, co podepisujete. Když ho firma
      překročí, relay zprávu odmítne a vy to uvidíte jako porušení.</p>
    </div>
    <div class="row2">
      <button class="btn btn--ghost" data-action="cancel">Zrušit</button>
      <button class="btn" data-action="confirm-subscribe">${verified ? 'Přihlásit se' : 'Přihlásit se přesto'}</button>
    </div>`;
}

function viewSettings() {
  return `
    <div class="card">
      <h2>Vaše identita</h2>
      <div class="mono dim" style="margin:7px 0 13px">${esc(vault.npub)}</div>
      ${vault.state.backedUpAt
        ? `<div class="badge badge--ok">zazálohováno ${esc(formatDate(vault.state.backedUpAt))}</div>`
        : '<div class="badge badge--danger">bez zálohy</div>'}
    </div>

    <div class="card">
      <h2>Zabezpečená záloha</h2>
      <p class="dim small">Stáhne se soubor zašifrovaný vaším heslem. Je bezpečné ho
      uložit do cloudu nebo poslat sám sobě e-mailem — bez hesla je nepoužitelný.
      Heslo si nikam neukládáme a nelze ho obnovit.</p>
      <label for="pass">Heslo pro zálohu (min. ${MIN_PASSPHRASE_LENGTH} znaků)</label>
      <input type="text" id="pass" placeholder="dlouhá fráze, kterou si zapamatujete"
             autocomplete="new-password" spellcheck="false">
      <div style="height:12px"></div>
      <button class="btn" data-action="download-backup">Stáhnout zálohu</button>
    </div>

    <div class="card">
      <h3 style="margin-top:0">Klíč v čitelné podobě</h3>
      <p class="dim small">Jen pokud ho chcete přenést ručně. Kdokoli, kdo ho uvidí,
      získá vaše odběry.</p>
      <button class="btn btn--ghost" data-action="show-nsec">Zobrazit tajný klíč</button>
      <div id="nsec-area"></div>
    </div>

    <div class="note note--warn">
      <strong>Klíč je v úložišti prohlížeče</strong>
      V pořádku pro ukázku, ne pro reálné použití — skutečná appka drží klíč
      v systémovém trezoru za biometrikou.
    </div>
    <div class="card">
      <h3 style="margin-top:0">Přečteno, hvězdičky, archiv</h3>
      <p class="small dim" style="margin:0">Zůstává jen v tomto zařízení. Synchronizace
      by znamenala poslat na server, které zprávy jste otevřel — což je přesně to
      sledování, kterému se anonymní odběr vyhýbá.</p>
    </div>
    ${installPrompt ? `<button class="btn btn--ghost" data-action="install">Přidat na plochu</button><div style="height:12px"></div>` : ''}
    <div class="card">
      <h3 style="margin-top:0">Relay</h3>
      <div class="mono dim">${esc(relays().join(', '))}</div>
    </div>
    <button class="btn btn--danger" data-action="wipe">Smazat identitu a odběry</button>`;
}

// ------------------------------------------------------------- helpers

function typeLabel(type) {
  return { newsletter: 'newsletter', transactional: 'transakční', alerts: 'upozornění' }[type] ?? type;
}

function formatBytes(size) {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(0)} kB`;
  return `${(size / 1024 / 1024).toFixed(1)} MB`;
}

/** Deterministic avatar from the sender's key, so it is stable and recognisable. */
function avatarFor(pubkey, name) {
  let hash = 0;
  for (let i = 0; i < pubkey.length; i++) hash = (hash * 31 + pubkey.charCodeAt(i)) >>> 0;
  const hue = hash % 360;
  const initial = (String(name ?? '?').trim()[0] ?? '?').toUpperCase();
  return { color: `hsl(${hue} 42% 38%)`, initial };
}

async function openInvite(uri) {
  let target;
  try { target = parseUri(uri.trim()); }
  catch { return toast('To není platný herald: odkaz', 'error'); }

  toast('Ověřuji firmu…');
  const info = await client.inspect(uri.trim());

  // If the invite named a channel this sender does not publish, offer a choice
  // rather than quietly substituting one -- otherwise the user signs terms for
  // a channel they never asked for.
  if (info.requestedChannelMissing) {
    if (!info.channels.length) return toast('Tato firma nezveřejňuje žádné kanály', 'error');
    return go('pick', { info, missing: info.requestedChannel });
  }
  const channel = info.selected
    ?? (info.channels.length === 1 ? info.channels[0] : null);
  if (!channel) {
    if (!info.channels.length) return toast('Tato firma nezveřejňuje žádné kanály', 'error');
    return go('pick', { info });
  }
  go('consent', { info, channel, token: info.token });
}

async function doSubscribe(button) {
  const { info, channel, token } = ctx;
  await withBusy(button, async () => {
    const res = await client.subscribe({ channel, relays: info.relays, ref: token ?? null });
    if (!res.accepted) {
      return toast(res.results.find((r) => !r.ok)?.message ?? 'žádný relay souhlas nepřijal', 'error');
    }
    const receiptVerified = res.receipt ? verifyConsent({
      subscribe: res.subscribe, receipt: res.receipt, rootPubkey: info.root,
      manifestEvent: info.manifestEvent, channel,
    }).ok : false;

    const record = {
      root: info.root, npub: info.npub, senderName: info.profile?.name ?? null,
      domain: info.domain, domainVerified: info.domainVerified,
      slug: channel.slug, channelName: channel.name, type: channel.type,
      privacy: channel.privacy, cap: channel.cap, currentCap: channel.cap,
      retentionDays: channel.retentionDays, terms: termsDigest(channel), relays: info.relays,
      subscribeId: res.subscribe.id, subscribeEvent: res.subscribe,
      subscribedAt: res.subscribe.created_at,
      receiptId: res.receipt?.id ?? null, receiptVerified, termsChanged: false,
    };
    const list = vault.state.subscriptions ??= [];
    const i = list.findIndex((s) => s.root === record.root && s.slug === record.slug);
    if (i === -1) list.push(record); else list[i] = record;
    vault.save();

    tab('inbox');
    toast(`Přihlášeno k ${channel.name}`, 'ok');
    void refresh({ quiet: true });
  });
}

async function doUnsubscribe(button) {
  const s = ctx.sub;
  if (!confirm(`Odhlásit se z „${s.channelName ?? s.slug}“?`)) return;
  await withBusy(button, async () => {
    const res = await client.unsubscribe({
      sender: s.root, slug: s.slug, subscribeId: s.subscribeId, relays: s.relays,
    });
    if (!res.accepted) return toast('Odhlášení se nepodařilo odeslat', 'error');
    vault.state.subscriptions = subs().filter((x) => !(x.root === s.root && x.slug === s.slug));
    vault.save();
    messages = messages.filter((m) => !(m.sub.root === s.root && m.sub.slug === s.slug));
    tab('senders');
    toast('Odhlášeno', 'ok');
  });
}

async function doDownload(index, button) {
  const attachment = ctx.message?.attachments?.[index];
  if (!attachment) return;
  await withBusy(button, async () => {
    try {
      const { bytes, filename, mime } = await client.downloadAttachment(attachment, {
        relay: ctx.message.sub.relays[0],
      });
      // The mime comes from the signed descriptor, and the bytes were hash-verified,
      // so building a blob URL here is safe -- unlike trusting the relay's headers.
      const url = URL.createObjectURL(new Blob([bytes], { type: mime }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      toast('Staženo a ověřeno', 'ok');
    } catch (err) {
      toast(err.message, 'error');
    }
  });
}

// ------------------------------------------------------------- scanner

let scanStop = null;

async function startScanner() {
  const status = $('#cam-status');
  if (!status) return;
  if (!window.isSecureContext) {
    $('#cam').hidden = true;
    status.textContent = 'Kamera vyžaduje HTTPS. Vložte odkaz ručně níže.';
    return;
  }
  if (!('BarcodeDetector' in window)) {
    $('#cam').hidden = true;
    status.textContent = 'Tento prohlížeč neumí čtení QR kódu (Safari ho nepodporuje). Vložte odkaz ručně níže.';
    return;
  }
  try {
    const detector = new window.BarcodeDetector({ formats: ['qr_code'] });
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
    const video = $('#cam');
    video.srcObject = stream;
    await video.play();
    status.textContent = 'Namiřte kameru na QR kód…';

    let active = true;
    scanStop = () => { active = false; stream.getTracks().forEach((t) => t.stop()); scanStop = null; };

    const tick = async () => {
      if (!active) return;
      try {
        const hit = (await detector.detect(video)).map((c) => c.rawValue)
          .find((v) => v?.startsWith('herald:'));
        if (hit) {
          scanStop();
          return openInvite(hit).catch((err) => toast(err.message, 'error'));
        }
      } catch { /* transient decode failures are normal */ }
      requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  } catch (err) {
    $('#cam').hidden = true;
    status.textContent = `Kameru nelze použít (${err.name}). Vložte odkaz ručně níže.`;
  }
}

// ------------------------------------------------------------- wiring

onAction(document.body, {
  back: () => { scanStop?.(); back(); },
  cancel: () => { scanStop?.(); back(); },
  tab: ({ value }) => { scanStop?.(); tab(value); },
  settings: () => go('settings'),
  refresh: () => refresh(),
  filter: ({ value }) => { filter = value; render(); },
  'clear-search': () => { query = ''; render(); },

  'create-identity': () => {
    vault.create({ subscriptions: [], flags: {}, relays: config.relays });
    connect(); tab('inbox');
  },
  'import-identity': () => {
    try {
      vault.importSecret($('#import').value, { subscriptions: [], flags: {}, relays: config.relays });
      connect(); tab('inbox'); toast('Identita importována', 'ok');
      void refresh({ quiet: true });
    } catch (err) { toast(err.message, 'error'); }
  },

  'paste-uri': (_, el) => { scanStop?.(); withBusy(el, () => openInvite($('#uri').value).catch((e) => toast(e.message, 'error'))); },
  pick: ({ slug }) => go('consent', { info: ctx.info, channel: ctx.info.channels.find((c) => c.slug === slug), token: ctx.info.token }, { replace: true }),
  'confirm-subscribe': (_, el) => doSubscribe(el),

  'open-message': ({ id }) => {
    const message = messages.find((m) => m.id === id);
    if (!message) return;
    setFlag(id, { read: true });
    go('message', { message });
  },
  'toggle-star': ({ id }) => { setFlag(id, { starred: !flagsFor(id).starred }); render(); },
  archive: ({ id }) => { setFlag(id, { archived: true, read: true }); back(); toast('Archivováno', 'ok'); },
  download: ({ index }, el) => doDownload(Number(index), el),

  'open-sender': ({ key }) => {
    const [root, slug] = key.split('|');
    const sub = subs().find((s) => s.root === root && s.slug === slug);
    if (!sub) return;
    const own = messages.filter((m) => m.sub.root === root && m.sub.slug === slug);
    go('sender', { sub, audit: client.auditChannel(own, sub.cap) });
  },
  unsubscribe: (_, el) => doUnsubscribe(el),

  'pick-backup': () => $('#backupfile')?.click(),

  'download-backup': (_, el) => withBusy(el, async () => {
    const passphrase = $('#pass')?.value ?? '';
    try {
      // scrypt is deliberately slow; yield first so the button shows its busy state.
      await new Promise((r) => setTimeout(r, 0));
      const envelope = encryptBackup(vault.secretKey, passphrase);
      const url = URL.createObjectURL(new Blob([JSON.stringify(envelope, null, 2)], { type: 'application/json' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = backupFilename(vault.npub);
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10000);

      vault.state.backedUpAt = Math.floor(Date.now() / 1000);
      vault.save();
      render();
      toast('Záloha stažena', 'ok');
    } catch (err) { toast(err.message, 'error'); }
  }),

  'restore-backup': (_, el) => withBusy(el, async () => {
    const passphrase = $('#restore-pass')?.value ?? '';
    try {
      await new Promise((r) => setTimeout(r, 0));
      const secretKey = decryptBackup(ctx.backupFile, passphrase);
      vault.importSecret(secretKey, {
        subscriptions: [], flags: {}, relays: config.relays,
        backedUpAt: Math.floor(Date.now() / 1000),
      });
      connect();
      tab('inbox');
      toast('Identita obnovena ze zálohy', 'ok');
      void refresh({ quiet: true });
    } catch (err) { toast(err.message, 'error'); }
  }),

  'show-nsec': () => {
    $('#nsec-area').innerHTML = `<div class="note note--danger" style="margin-top:11px">
      <strong>Nikomu tento klíč neukazujte</strong>
      <span class="mono">${esc(vault.nsec)}</span></div>`;
  },
  install: async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    installPrompt = null;
    render();
  },
  wipe: () => {
    if (!confirm('Smazat identitu a všechny odběry? Nelze vrátit.')) return;
    vault.destroy();
    stack.length = 0;
    messages = [];
    render();
  },
});

document.body.addEventListener('change', async (event) => {
  if (event.target.id !== 'backupfile' || !event.target.files?.[0]) return;
  const file = event.target.files[0];
  event.target.value = '';
  try {
    const doc = JSON.parse(await file.text());
    if (doc.herald_backup !== 1) throw new Error('to není záloha Herald klíče');
    ctx.backupFile = doc;
    $('#restore-area').innerHTML = `
      <div class="note note--ok" style="margin-top:12px">
        <strong>Záloha načtena</strong>
        Identita <span class="mono">${esc(doc.npub ?? 'neznámá')}</span>
      </div>
      <label for="restore-pass">Heslo k záloze</label>
      <input type="text" id="restore-pass" autocomplete="current-password" spellcheck="false">
      <div style="height:12px"></div>
      <button class="btn" data-action="restore-backup">Obnovit identitu</button>`;
  } catch (err) {
    $('#restore-area').innerHTML = `<div class="note note--danger" style="margin-top:12px">${esc(err.message)}</div>`;
  }
});

// Search is typed, so it needs an input listener rather than a click handler.
document.body.addEventListener('input', (event) => {
  if (event.target.id !== 'q') return;
  query = event.target.value;
  // Re-render only the list, so the field does not lose focus mid-typing.
  const host = $('#screen');
  if (screen === 'inbox') host.innerHTML = viewInbox();
});

// Configuration must be in hand before a relay connection or a domain check.
loadConfig().then((loaded) => {
  config = loaded;
  if (vault.exists) {
    connect();
    // Set before the first paint, so an inbox with subscriptions never renders
    // its empty state before anything has been fetched.
    loading = subs().length > 0;
    render();
    void refresh({ quiet: true });
  } else {
    render();
  }
}).catch(() => render());

window.addEventListener('beforeinstallprompt', (event) => {
  event.preventDefault();
  installPrompt = event;
  if (screen === 'settings') render();
});

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').catch(() => { /* offline support is optional */ });
  });
}
